import type { ClientMsg, LatLng, ServerMsg } from '@konkride/shared';
import { API, api } from './api';

export type ConnState = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';
type Handlers = { onMsg: (m: ServerMsg) => void; onState: (s: ConnState) => void; onKicked: () => void; getPresence: () => ClientMsg & { t: 'presence' }; getPos: () => LatLng | null; onTrip: () => boolean };

/**
 * One live connection to the city hub. A fresh single-use ticket is requested for every (re)connect.
 * Pings carry location: every 30 s when idle, every 5 s on a trip. Reconnects with jittered exponential backoff.
 */
export class Live {
  private ws: WebSocket | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private retry = 0;
  private stopped = true;
  constructor(private role: 'driver' | 'rider', private city: string, private h: Handlers, private pingIdle = 30, private pingTrip = 5) {}

  start() { this.stopped = false; void this.open(); }
  stop() { this.stopped = true; if (this.timer) clearTimeout(this.timer); this.timer = null; this.ws?.close(1000, 'bye'); this.ws = null; this.h.onState('closed'); }
  send(m: ClientMsg) { if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m)); }
  /** Push the latest presence now (after the user changes a setting). */
  presence() { this.send(this.h.getPresence()); }

  private async open() {
    if (this.stopped) return;
    this.h.onState(this.retry ? 'reconnecting' : 'connecting');
    try {
      const { ticket } = await api<{ ticket: string }>('/ws/ticket', { json: { role: this.role, city: this.city } });
      if (this.stopped) return;
      const ws = new WebSocket(`${API.replace(/^http/, 'ws')}/ws?ticket=${encodeURIComponent(ticket)}`);
      this.ws = ws;
      ws.onopen = () => { this.retry = 0; this.h.onState('open'); this.presence(); this.schedulePing(); };
      ws.onmessage = (e) => { try { this.h.onMsg(JSON.parse(e.data as string) as ServerMsg); } catch { /* ignore malformed */ } };
      ws.onclose = (e) => {
        if (this.timer) clearTimeout(this.timer);
        if (this.ws === ws) this.ws = null;
        if (e.code === 4000) { this.stopped = true; this.h.onState('closed'); this.h.onKicked(); return; } // opened on another device
        this.reconnect();
      };
      ws.onerror = () => { /* onclose follows */ };
    } catch (err: any) {
      if (err?.status === 401 || err?.status === 403) { this.stopped = true; this.h.onState('closed'); return; } // signed out, suspended or not approved
      this.reconnect();
    }
  }
  private reconnect() {
    if (this.stopped) return;
    this.h.onState('reconnecting');
    const delay = Math.min(15_000, 500 * 2 ** this.retry++) * (0.7 + Math.random() * 0.6);
    this.timer = setTimeout(() => void this.open(), delay);
  }
  private schedulePing() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      const pos = this.h.getPos();
      if (pos) this.send({ t: 'ping', pos });
      this.schedulePing();
    }, (this.h.onTrip() ? this.pingTrip : this.pingIdle) * 1000);
  }
}
